import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ArrowRight, BookOpen, Target, Zap, MessageSquare,
  Image, Video, PenTool, Search, ChevronRight, Sparkles
} from 'lucide-react';

const AcademyStartHere = () => {
  const navigate = useNavigate();

  const paths = [
    {
      title: "I want to write better content",
      description: "Learn AI tools for writing emails, social posts, blogs, and marketing copy",
      icon: PenTool,
      color: "bg-blue-500",
      courses: ["ChatGPT Mastery", "Claude AI Complete Guide", "Jasper AI for Marketing"],
      recommended: "chatgpt-mastery"
    },
    {
      title: "I want to create images",
      description: "Master AI image generation for marketing, products, and social media",
      icon: Image,
      color: "bg-purple-500",
      courses: ["Midjourney Image Creation"],
      recommended: "midjourney-mastery"
    },
    {
      title: "I want to make videos",
      description: "Create professional videos using AI without expensive equipment",
      icon: Video,
      color: "bg-red-500",
      courses: ["Veo: AI Video Creation"],
      recommended: "veo-video-creation"
    },
    {
      title: "I want to build apps/websites",
      description: "Create functional apps and websites without coding experience",
      icon: Zap,
      color: "bg-green-500",
      courses: ["Lovable: Build Apps Without Code"],
      recommended: "lovable-app-builder"
    },
    {
      title: "I want to research faster",
      description: "Find, verify, and synthesize information efficiently with AI",
      icon: Search,
      color: "bg-orange-500",
      courses: ["Perplexity: AI-Powered Research"],
      recommended: "perplexity-research"
    },
    {
      title: "I want to boost productivity",
      description: "Integrate AI into your daily workflow for maximum efficiency",
      icon: Target,
      color: "bg-teal-500",
      courses: ["Google Gemini Complete Guide"],
      recommended: "gemini-ai-guide"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="academy-start-here">
      <Navbar />

      {/* Hero */}
      <div className="pt-16 bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 mb-6">
            <Sparkles className="w-4 h-4 mr-2" />
            Start Here
          </Badge>
          
          <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
            Find Your AI Learning Path
          </h1>
          
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Not sure where to begin? Answer one question and we'll recommend the perfect starting point for you.
          </p>
        </div>
      </div>

      {/* Path Selection */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="font-heading text-2xl font-bold text-slate-900 text-center mb-4">
          What do you want to achieve?
        </h2>
        <p className="text-slate-600 text-center mb-8">
          Click on your primary goal to see our recommended learning path
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          {paths.map((path, idx) => (
            <Card 
              key={idx}
              className="group cursor-pointer hover:shadow-xl hover:border-primary/50 transition-all duration-300"
              onClick={() => navigate(`/academy/course/${path.recommended}`)}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 ${path.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <path.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-lg text-slate-900 mb-1 group-hover:text-primary transition-colors">
                      {path.title}
                    </h3>
                    <p className="text-sm text-slate-600 mb-3">
                      {path.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {path.courses.map((course, cidx) => (
                        <Badge key={cidx} variant="outline" className="text-xs">
                          {course}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-primary group-hover:translate-x-1 transition-all" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Learning Tips */}
      <div className="bg-white border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="font-heading text-2xl font-bold text-slate-900 text-center mb-8">
            Tips for Success
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: "Start with one tool",
                description: "Master one AI tool before moving to the next. Deep knowledge beats surface-level familiarity."
              },
              {
                title: "Practice daily",
                description: "Spend 15-30 minutes each day applying what you learn. Consistency is key to building AI skills."
              },
              {
                title: "Complete the quizzes",
                description: "Don't skip the quizzes! They help reinforce your learning and identify knowledge gaps."
              }
            ].map((tip, idx) => (
              <Card key={idx} className="text-center p-6">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="font-bold text-primary">{idx + 1}</span>
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{tip.title}</h3>
                <p className="text-sm text-slate-600">{tip.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="bg-gradient-to-r from-primary to-purple-600 text-white p-8 text-center">
          <h3 className="font-heading text-2xl font-bold mb-4">
            Ready to start learning?
          </h3>
          <p className="text-white/80 mb-6 max-w-xl mx-auto">
            Get unlimited access to all 8 courses with an AI Academy membership. First lessons are always free.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-primary hover:bg-slate-100"
              onClick={() => navigate('/academy/courses')}
            >
              Browse Free Lessons
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10"
              onClick={() => navigate('/academy/membership')}
            >
              Get Membership
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AcademyStartHere;
